<template>
    <div
        class="-mt-2 z-0 overflow-y-auto border border-[#E7E5E4] rounded-b-[16px] bg-white"
    >
        <div role="tablist" class="border-b border-[#E7E5E4] px-2 min-[320px]:px-4 flex flex-wrap gap-2 sticky top-0 bg-white z-[100] py-3 shadow-sm mb-0 min-h-[48px] items-center">
                <button
                    role="tab"
                    :aria-selected="tab === 'episodes' ? 'true' : 'false'"
                    aria-controls="episodes-tabpanel"
                    id="episodes-tab"
                    :tabindex="tab === 'episodes' ? 0 : -1"
                    class="text-player-list-item text-[14px] font-bold leading-[22px] p-2 border-b-2 -mb-[1px] shrink-0 min-w-[80px] text-center"
                    :class="
                        tab === 'episodes'
                            ? 'border-player-list-item'
                            : 'border-transparent'
                    "
                    @click="tab = 'episodes'"
                    @keydown="handleTabKeydown"
                >
                    {{ __("Episodes") }}
                </button>
                <button
                    v-if="podcast.transcript"
                    role="tab"
                    :aria-selected="tab === 'transcript' ? 'true' : 'false'"
                    aria-controls="transcript-tabpanel"
                    id="transcript-tab"
                    :tabindex="tab === 'transcript' ? 0 : -1"
                    class="text-player-list-item text-[14px] font-bold leading-[22px] p-2 border-b-2 -mb-[1px] shrink-0 min-w-[100px] text-center"
                    :class="
                        tab === 'transcript'
                            ? 'border-player-list-item'
                            : 'border-transparent'
                    "
                    @click="tab = 'transcript'"
                    @keydown="handleTabKeydown"
                >
                    {{ __("Transcript") }}
                </button>
        </div>
        <div class="h-full relative pt-2">
            <ul
                v-if="tab === 'episodes'"
                role="tabpanel"
                id="episodes-tabpanel"
                aria-labelledby="episodes-tab"
                tabindex="0"
                class="px-2 xs:px-4 pt-2 pb-6"
                :title="__('Episodes')"
                :aria-label="__('Episodes')"
            >
                <li
                    v-for="(item, index) in playlist"
                    :key="index"
                    class="w-full"
                >
                    <button
                        :ref="el => { if (el) episodeButtons[index] = el }"
                        :title="item.title"
                        :aria-label="item.title"
                        :aria-current="isCurrent(index) ? 'true' : undefined"
                        @click="emit('play-track', index)"
                        class="w-full py-2 px-2 text-left text-player-list-item hover:text-gray-900 hover:bg-gray-200 border-l-2 border-transparent hover:border-gray-500 transition focus:outline-none focus:bg-gray-200 focus:border-gray-500"
                        :class="{
                            'text-player-accent':
                                isPlaying(index) || isCurrent(index),
                        }"
                        tabindex="0"
                    >
                        <div class="flex flex-row items-center w-full">
                            <div class="hidden min-[320px]:block">
                                <PauseCircle
                                    v-if="isPlaying(index)"
                                    class="fill-player-list-item"
                                />
                                <PlayCircle
                                    v-else
                                    :class="{
                                        'fill-player-accent': isCurrent(index),
                                    }"
                                    class="fill-player-list-item"
                                />
                            </div>

                            <p
                                class="min-[320px]:ml-2 text-left text-[14px] font-normal"
                            >
                                <template v-if="podcast.season">
                                    <span v-if="item.season"
                                        >S{{ item.season }} /
                                    </span>
                                    <span v-if="item.episode">E{{ item.episode }}</span>
                                </template>
                                <span class="ml-1" v-text="item.title" />
                            </p>

                            <p
                                class="text-right text-[14px] font-normal ml-auto opacity-60"
                                v-text="formatTime(item.audio.duration)"
                            />
                        </div>
                    </button>

                    <div
                        ref="chapters"
                        v-if="isCurrent(index) && item.chapters?.length"
                        class="flex flex-wrap items-center gap-2 min-[320px]:pl-6 mt-3 border-b border-[#E7E5E4] pb-5"
                    >
                        <button
                            v-for="(chapter, chapterIndex) in item.chapters ??
                            []"
                            class="border border-[#E7E5E4] text-player-list-item rounded-[6px] py-1 px-2.5 text-[13px] font-normal hover:border-[#EB5AA9] transition whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-player-accent"
                            :class="{
                                'border-player-accent':
                                    currentChapter === chapterIndex,
                            }"
                            :data-chapter="chapterIndex"
                            @click.stop="
                                emit('play-chapter', chapter.from_time)
                            "
                        >
<!--                            <span class="font-bold">{{-->
<!--                                formatTime(chapter.from_time)-->
<!--                            }}</span>-->
                            <span class="truncate">{{ chapter.title }}</span>
                        </button>
                    </div>
                </li>
            </ul>

            <!-- Transcript display -->
            <div 
                v-else 
                role="tabpanel"
                id="transcript-tabpanel"
                aria-labelledby="transcript-tab"
                tabindex="0"
                class="px-4 py-4 overflow-hidden max-h-[200px]"
            >
                <div class="text-center text-gray-700 py-8" v-if="loadingTranscription">{{ __("Loading...") }}</div>
                <template v-else>
                    <div v-if="transcriptionEntries.length === 0" class="text-center text-gray-700 py-8">
                        {{ __("No transcript found") }}
                    </div>
                    <ul v-else>
                        <li
                            v-for="(entry, idx) in transcriptionEntries"
                            :key="idx"
                            :class="[
                                'flex items-center p-2 rounded-md transition-colors duration-75',
                                idx === currentTranscriptIndex ? 'bg-gray-100 transcript-line-active' : '',
                            ]"
                        >
                            <span class="text-[14px] font-medium text-player-list-item w-12 shrink-0 bg-[#fdeef6] rounded text-center">
                                {{ formatTranscriptTime(entry.start) }}
                            </span>
                            <span class="ml-2 text-[16px] text-player-list-item">
                                {{ entry.text }}
                            </span>
                        </li>
                    </ul>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { formatTime } from "../useWaveform";
import __ from "../helpers";
import PlayCircle from "./PlayCircle.vue";
import PauseCircle from "./PauseCircle.vue";
import { ref, watch, computed, nextTick } from "vue";
import axios from "axios";
import { usePage } from "@inertiajs/vue3";
import { shiftCues } from "../../Shared/vtt.js";

const props = defineProps({
    podcast: Object,
    playlist: Array,
    currentTrack: Number,
    isPlaying: Boolean,
    currentChapter: Number,
    podcastId: String,
    currentTime: Number,
});

const emit = defineEmits(["play-track", "play-chapter"]);

watch(
    () => props.currentTrack,
    () => {
        scrollToChapter(props.currentChapter);
    }
);

watch(
    () => props.currentChapter,
    () => {
        scrollToChapter(props.currentChapter);
    }
);

function isCurrent(index) {
    return props.currentTrack === index;
}

function isPlaying(index) {
    return isCurrent(index) && props.isPlaying;
}

const chapters = ref(null);

function scrollToChapter(index) {
    if (!chapters.value && !chapters.value?.length) {
        return;
    }

    const chapter = chapters.value[0]?.querySelector(
        `button[data-chapter="${index}"]`
    );

    if (chapter) {
        chapters.value[0].scrollTo({
            left: chapter.offsetLeft - chapters.value[0].offsetLeft,
            behavior: "smooth",
        });
    }
}

const tab = ref("episodes");

function handleTabKeydown(event) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const newTab = tab.value === 'episodes' ? 'transcript' : 'episodes';
        if (newTab === 'transcript' && !props.podcast.transcript) return;
        tab.value = newTab;
        nextTick(() => {
            document.getElementById(`${newTab}-tab`)?.focus();
        });
    }
}
const transcription = ref("");
const transcriptionEntries = ref([]);
const loadingTranscription = ref(false);
const episodeButtons = ref({});

function focusEpisode(index) {
    nextTick(() => {
        const button = episodeButtons.value[index];
        if (button && button.focus) {
            button.focus();
        }
    });
}

defineExpose({
    tab,
    getCurrentTab: () => tab.value,
    focusEpisode
});

const currentTranscriptIndex = computed(() => {
    let idx = -1;
    for (let i = 0; i < transcriptionEntries.value.length; i++) {
        if (props.currentTime >= transcriptionEntries.value[i].start) {
            idx = i;
        } else {
            break;
        }
    }
    return idx;
});

watch([tab, () => props.currentTrack], async ([newTab]) => {
    if (newTab === "transcript") {
        await fetchTranscription();
    }
});

const page = usePage();

async function fetchTranscription() {
    transcription.value = "";
    transcriptionEntries.value = [];
    loadingTranscription.value = true;
    const episode = props.playlist[props.currentTrack];
    if (!episode) return;
    try {
        const url = route("podcast.transcription", {
            podcast: props.podcastId,
            episode: episode.hash_id,
            // Members-only show: the website's member signature (mexp/msig)
            ...(page.props.memberQuery ?? {}),
        });
        const response = await axios.get(url);
        transcription.value = response.data;
        transcriptionEntries.value = shiftCues(parseVTT(response.data), episode.timeline?.insertions);
    } catch (e) {
        transcription.value = "Failed to load transcription.";
        transcriptionEntries.value = [];
    }
    loadingTranscription.value = false;
}

function parseVTT(vtt) {
    const lines = vtt.split('\n');
    const entries = [];
    let i = 0;
    while (i < lines.length) {
        if (lines[i].includes('-->')) {
            const [start, end] = lines[i].split(' --> ').map(s => s.trim());
            let text = '';
            i++;
            while (lines[i] && !lines[i].includes('-->')) {
                text += lines[i] + ' ';
                i++;
            }
            // Remove speaker tags like <v Speaker A> and </v>
            const cleanText = text.replace(/<v[^>]*>/g, '').replace(/<\/v>/g, '').trim();
            entries.push({
                start: toSeconds(start),
                end: toSeconds(end),
                text: cleanText
            });
        } else {
            i++;
        }
    }
    return entries;
}

function toSeconds(time) {
    const parts = time.split(':');
    let seconds = 0;
    if (parts.length === 3) {
        seconds = (+parts[0]) * 3600 + (+parts[1]) * 60 + parseFloat(parts[2].replace(',', '.'));
    } else if (parts.length === 2) {
        seconds = (+parts[0]) * 60 + parseFloat(parts[1].replace(',', '.'));
    }
    return seconds;
}

watch(currentTranscriptIndex, async (newIdx) => {
    await nextTick();
    const el = document.querySelector('.transcript-line-active');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

function formatTranscriptTime(seconds) {
    seconds = Math.floor(seconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    if (h > 0) {
        return [h, m, s].map(n => n.toString().padStart(2, '0')).join(':');
    } else {
        return [m, s].map(n => n.toString().padStart(2, '0')).join(':');
    }
}
</script>
